import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

// ============ Types ============

export type UserState = {
  email?: string;
  name: string;
  referral: string | null;
  level: string;
};

export type SendState = {
  from: string | `0x${string}`;
  to: string | `0x${string}`;
  value: string;
};

export type AuthState = {
  auth: string;
  point: string;
  date?: string;
};

export type TermsState = {
  mandatory?: boolean;
  optional: boolean;
};

export type SendNftState = {
  fromAddr: string | null;
  toAddr: string | null;
  contract: string | undefined;
  tokenId: string | null;
  tokenType: string;
};

export type JoinState = {
  email: string;
};

export type PinState = {
  pinNum: string;
  path: string;
  active: boolean;
};

export type ResetPwState = {
  email: string;
  emailAuth: string;
};

export type OutLinkState = {
  path: string;
  toAddr: string | `0x${string}`;
  amount: string;
  symbol: string;
  chain: string;
};

export type TotalAmtState = {
  amount: string;
  chainId: number;
};

// ============ Store Interface ============

interface AppStore {
  // User Info
  userInfo: UserState;
  setUserInfo: (state: UserState) => void;
  deleteUserInfo: () => void;

  // Send Info
  sendInfo: SendState;
  setSendInfo: (state: SendState) => void;

  // Auth Info
  authInfo: AuthState;
  setAuthInfo: (state: AuthState) => void;

  // Terms Info
  termsInfo: TermsState;
  setTermsInfo: (state: TermsState) => void;

  // Send NFT Info
  sendNftInfo: SendNftState;
  setSendNftInfo: (state: SendNftState) => void;

  // Join Info
  joinInfo: JoinState;
  setJoinInfo: (state: JoinState) => void;

  // Pin Info
  pinInfo: PinState;
  setPinInfo: (state: PinState) => void;

  // Reset Password Info
  resetPwInfo: ResetPwState;
  setResetPwInfo: (state: ResetPwState) => void;

  // OutLink Info
  outLinkInfo: OutLinkState;
  setOutLinkInfo: (state: OutLinkState) => void;

  // Total Amount Info
  totalAmtInfo: TotalAmtState;
  setTotalAmtInfo: (state: TotalAmtState) => void;
}

// ============ Initial States ============

const initialUserInfo: UserState = {
  email: '',
  name: 'member',
  referral: null,
  level: '1',
};

const initialSendInfo: SendState = {
  from: '',
  to: '',
  value: '',
};

const initialAuthInfo: AuthState = {
  auth: '',
  point: '',
  date: '',
};

const initialTermsInfo: TermsState = {
  mandatory: true,
  optional: false,
};

const initialSendNftInfo: SendNftState = {
  fromAddr: null,
  toAddr: null,
  contract: undefined,
  tokenId: null,
  tokenType: '',
};

const initialJoinInfo: JoinState = {
  email: '',
};

const initialPinInfo: PinState = {
  pinNum: '',
  path: '',
  active: false,
};

const initialResetPwInfo: ResetPwState = {
  email: '',
  emailAuth: '',
};

const initialOutLinkInfo: OutLinkState = {
  path: '',
  toAddr: '',
  amount: '',
  symbol: '',
  chain: '',
};

const initialTotalAmtInfo: TotalAmtState = {
  amount: '0',
  chainId: 0,
};

// ============ Create Store ============

export const useAppStore = create<AppStore>()((set) => ({
  // User Info
  userInfo: initialUserInfo,
  setUserInfo: (state) => set({ userInfo: state }),
  deleteUserInfo: () => set({ userInfo: initialUserInfo }),

  // Send Info
  sendInfo: initialSendInfo,
  setSendInfo: (state) => set({ sendInfo: state }),

  // Auth Info
  authInfo: initialAuthInfo,
  setAuthInfo: (state) => set({ authInfo: state }),

  // Terms Info
  termsInfo: initialTermsInfo,
  setTermsInfo: (state) => set({ termsInfo: state }),

  // Send NFT Info
  sendNftInfo: initialSendNftInfo,
  setSendNftInfo: (state) => set({ sendNftInfo: state }),

  // Join Info
  joinInfo: initialJoinInfo,
  setJoinInfo: (state) => set({ joinInfo: state }),

  // Pin Info
  pinInfo: initialPinInfo,
  setPinInfo: (state) => set({ pinInfo: state }),

  // Reset Password Info
  resetPwInfo: initialResetPwInfo,
  setResetPwInfo: (state) => set({ resetPwInfo: state }),

  // OutLink Info
  outLinkInfo: initialOutLinkInfo,
  setOutLinkInfo: (state) => set({ outLinkInfo: state }),

  // Total Amount Info
  totalAmtInfo: initialTotalAmtInfo,
  setTotalAmtInfo: (state) => set({ totalAmtInfo: state }),
}));

// ============ Selector Hooks (Redux 호환용) ============

export const useUserInfo = () => useAppStore((state) => state.userInfo);
export const useSendInfo = () => useAppStore((state) => state.sendInfo);
export const useAuthInfo = () => useAppStore((state) => state.authInfo);
export const useTermsInfo = () => useAppStore((state) => state.termsInfo);
export const useSendNftInfo = () => useAppStore((state) => state.sendNftInfo);
export const useJoinInfo = () => useAppStore((state) => state.joinInfo);
export const usePinInfo = () => useAppStore((state) => state.pinInfo);
export const useResetPwInfo = () => useAppStore((state) => state.resetPwInfo);
export const useOutLinkInfo = () => useAppStore((state) => state.outLinkInfo);
export const useTotalAmtInfo = () => useAppStore((state) => state.totalAmtInfo);

// ============ Action Hooks ============

export const useUserInfoActions = () => useAppStore(
  useShallow((state) => ({
    setUserInfo: state.setUserInfo,
    deleteUserInfo: state.deleteUserInfo,
  }))
);

export const useSendInfoActions = () => useAppStore(
  useShallow((state) => ({
    setSendInfo: state.setSendInfo,
  }))
);

export const useAuthInfoActions = () => useAppStore(
  useShallow((state) => ({
    setAuthInfo: state.setAuthInfo,
  }))
);

export const useTermsInfoActions = () => useAppStore(
  useShallow((state) => ({
    setTermsInfo: state.setTermsInfo,
  }))
);

export const useSendNftInfoActions = () => useAppStore(
  useShallow((state) => ({
    setSendNftInfo: state.setSendNftInfo,
  }))
);

export const useJoinInfoActions = () => useAppStore(
  useShallow((state) => ({
    setJoinInfo: state.setJoinInfo,
  }))
);

export const usePinInfoActions = () => useAppStore(
  useShallow((state) => ({
    setPinInfo: state.setPinInfo,
  }))
);

export const useResetPwInfoActions = () => useAppStore(
  useShallow((state) => ({
    setResetPwInfo: state.setResetPwInfo,
  }))
);

export const useOutLinkInfoActions = () => useAppStore(
  useShallow((state) => ({
    setOutLinkInfo: state.setOutLinkInfo,
  }))
);

export const useTotalAmtInfoActions = () => useAppStore(
  useShallow((state) => ({
    setTotalAmtInfo: state.setTotalAmtInfo,
  }))
);
